import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Calendar, Clock, MapPin, Users, UserRound, Check, X } from "lucide-react";
import { Service } from "../../../models/ServiceModel";
import useServicesService from "../../../services/useServicesService";
import "./styles/ServicePickerTable.css";

type ServicePickerFilters = {
  search: string;
  origin: string;
  destination: string;
  passengers: string;
  time_from: string;
  time_to: string;
};

type Props = {
  companyId?: number | null;
  serviceDate?: string | null;
  excludeIds?: number[];
  selectedIds?: number[];
  onSelect: (serviceId: number, service: Service) => void;
  onDeselect?: (serviceId: number) => void;
  multiSelect?: boolean;
};

const formatDate = (raw?: string | null) => {
  if (!raw) return "—";
  return String(raw).slice(0, 10);
};

const formatTime = (raw?: string | null) => {
  if (!raw) return "—";
  return String(raw).slice(0, 5);
};

const getPassengerDisplay = (service: Service): { count: number; names: string } => {
  // Intentar obtener detalles de pasajeros
  if (Array.isArray(service.passenger_details) && service.passenger_details.length > 0) {
    const names = service.passenger_details
      .map((p) => (p.name || "").trim())
      .filter(Boolean)
      .join(", ");
    return {
      count: service.passenger_details.length,
      names: names || "Sin nombres",
    };
  }

  // Si no hay detalles, intentar con campo passenger (string de nombres)
  if (typeof service.passenger === "string" && service.passenger.trim()) {
    const namesList = service.passenger.split(",").map((n) => n.trim()).filter(Boolean);
    return {
      count: namesList.length,
      names: service.passenger.trim(),
    };
  }

  // Si es un número, solo mostrar cantidad
  if (typeof service.passenger === "number") {
    return {
      count: service.passenger,
      names: `${service.passenger} pasajero${service.passenger !== 1 ? "s" : ""}`,
    };
  }

  // Si tiene array de passengers
  if (Array.isArray(service.passengers) && service.passengers.length > 0) {
    const names = service.passengers
      .map((p) => (typeof p === "string" ? p : (p as any).name || ""))
      .filter(Boolean)
      .join(", ");
    return {
      count: service.passengers.length,
      names: names || `${service.passengers.length} pasajero${service.passengers.length !== 1 ? "s" : ""}`,
    };
  }

  return { count: 0, names: "Sin pasajeros" };
};

const ServicePickerTable: React.FC<Props> = ({
  companyId,
  serviceDate,
  excludeIds = [],
  selectedIds = [],
  onSelect,
  onDeselect,
  multiSelect = false,
}) => {
  const { list } = useServicesService();
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<ServicePickerFilters>({
    search: "",
    origin: "",
    destination: "",
    passengers: "",
    time_from: "",
    time_to: "",
  });

  const requestRef = useRef<AbortController | null>(null);

  const updateFilter = <K extends keyof ServicePickerFilters>(
    key: K,
    value: ServicePickerFilters[K]
  ) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      search: "",
      origin: "",
      destination: "",
      passengers: "",
      time_from: "",
      time_to: "",
    });
  };

  const hasFilters = Boolean(
    filters.search || filters.origin || filters.destination || filters.passengers || filters.time_from || filters.time_to
  );

  const loadServices = useCallback(() => {
    requestRef.current?.abort();
    const ctrl = new AbortController();
    requestRef.current = ctrl;
    setLoading(true);

    const apiFilters: Record<string, string | number | boolean> = {
      page: 1,
      page_size: 200,
      general_mode: "true",
    };

    if (companyId) apiFilters.company_id = companyId;

    if (serviceDate) {
      apiFilters.start_date = serviceDate.slice(0, 10);
      apiFilters.end_date = serviceDate.slice(0, 10);
    }

    if (filters.search.trim()) apiFilters.search = filters.search.trim();
    if (filters.origin.trim()) apiFilters.origin = filters.origin.trim();
    if (filters.destination.trim()) apiFilters.destination = filters.destination.trim();
    if (filters.passengers.trim()) apiFilters.passengers = filters.passengers.trim();
    if (filters.time_from.trim()) apiFilters.time_from = filters.time_from.trim();
    if (filters.time_to.trim()) apiFilters.time_to = filters.time_to.trim();

    list(
      apiFilters,
      {
        onSuccess: (resp: any) => {
          if (ctrl.signal.aborted) return;
          const items: Service[] = Array.isArray(resp?.data?.services)
            ? resp.data.services
            : [];
          const excluded = new Set(excludeIds.map((id) => id));
          const filtered = items.filter((service) => service.id && !excluded.has(service.id));
          setServices(filtered);
          setLoading(false);
        },
        onError: () => {
          if (ctrl.signal.aborted) return;
          setServices([]);
          setLoading(false);
        },
      },
      ctrl.signal
    );
  }, [companyId, serviceDate, excludeIds, filters, list]);

  useEffect(() => {
    const delay = filters.search.trim() || filters.origin.trim() || filters.destination.trim() || filters.passengers.trim() ? 400 : 0;
    const timeout = setTimeout(() => loadServices(), delay);
    return () => {
      clearTimeout(timeout);
      requestRef.current?.abort();
    };
  }, [loadServices]);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const handleRowClick = (service: Service) => {
    if (!service.id) return;
    const isSelected = selectedSet.has(service.id);
    
    if (multiSelect) {
      if (isSelected && onDeselect) {
        onDeselect(service.id);
      } else if (!isSelected) {
        onSelect(service.id, service);
      }
    } else {
      if (!isSelected) {
        onSelect(service.id, service);
      } else if (onDeselect) {
        onDeselect(service.id);
      }
    }
  };

  return (
    <div className="service-picker-table-container">
      <div className="service-picker-table-toolbar">
        <div className="service-picker-table-title">
          <span className="service-picker-table-count">
            {services.length} servicio{services.length !== 1 ? "s" : ""} disponible
            {services.length !== 1 ? "s" : ""}
          </span>
          {selectedIds.length > 0 && (
            <span className="service-picker-selected-badge">
              {selectedIds.length} seleccionado{selectedIds.length !== 1 ? "s" : ""}
            </span>
          )}
          {hasFilters && (
            <button className="service-picker-clear-filters" onClick={clearFilters} type="button">
              Limpiar filtros
            </button>
          )}
        </div>

        <div className="service-picker-search-wrap">
          <input
            type="text"
            className="service-picker-search-input"
            placeholder="Buscar por ID, código, conductor…"
            value={filters.search}
            onChange={(e) => updateFilter("search", e.target.value)}
          />
        </div>

        <div className="service-picker-filters-grid">
          <label className="service-picker-filter-field">
            <span className="service-picker-filter-label">Origen</span>
            <input
              type="text"
              className="service-picker-filter-input"
              placeholder="Filtrar por origen…"
              value={filters.origin}
              onChange={(e) => updateFilter("origin", e.target.value)}
            />
          </label>

          <label className="service-picker-filter-field">
            <span className="service-picker-filter-label">Destino</span>
            <input
              type="text"
              className="service-picker-filter-input"
              placeholder="Filtrar por destino…"
              value={filters.destination}
              onChange={(e) => updateFilter("destination", e.target.value)}
            />
          </label>

          <label className="service-picker-filter-field">
            <span className="service-picker-filter-label">Pasajeros</span>
            <input
              type="text"
              className="service-picker-filter-input"
              placeholder="Filtrar por nombre de pasajero…"
              value={filters.passengers}
              onChange={(e) => updateFilter("passengers", e.target.value)}
            />
          </label>

          <div className="service-picker-filter-field service-picker-filter-time-range">
            <span className="service-picker-filter-label">Hora de servicio</span>
            <div className="service-picker-time-inputs">
              <input
                type="time"
                className="service-picker-filter-input service-picker-time-input"
                value={filters.time_from}
                onChange={(e) => updateFilter("time_from", e.target.value)}
              />
              <span className="service-picker-time-separator">—</span>
              <input
                type="time"
                className="service-picker-filter-input service-picker-time-input"
                value={filters.time_to}
                onChange={(e) => updateFilter("time_to", e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="service-picker-table-shell">
        <table className="service-picker-table">
          <thead>
            <tr>
              {multiSelect && <th className="col-check"></th>}
              <th>ID / Código</th>
              <th>Fecha / Hora</th>
              <th>Ruta</th>
              <th>Pasajeros</th>
              <th>Conductor</th>
              <th>Estado</th>
              <th className="col-actions"></th>
            </tr>
          </thead>
          <tbody>
            {loading && services.length === 0 ? (
              <tr>
                <td colSpan={multiSelect ? 8 : 7} className="service-picker-state-cell">
                  <div className="service-picker-loading">
                    <div className="spinner-pro" />
                    <span>Cargando servicios…</span>
                  </div>
                </td>
              </tr>
            ) : services.length === 0 ? (
              <tr>
                <td colSpan={multiSelect ? 8 : 7} className="service-picker-state-cell">
                  <div className="service-picker-empty">
                    <p>No se encontraron servicios</p>
                    <span>
                      {hasFilters
                        ? "Intenta ajustar los filtros"
                        : serviceDate
                          ? "No hay servicios disponibles para esta fecha"
                          : "No hay servicios disponibles"}
                    </span>
                  </div>
                </td>
              </tr>
            ) : (
              services.map((service) => {
                const isSelected = service.id ? selectedSet.has(service.id) : false;
                const enaexCode = (service.enaex_code || "").trim();
                const passengerInfo = getPassengerDisplay(service);
                
                return (
                  <tr
                    key={service.id}
                    className={`service-picker-row${isSelected ? " is-selected" : ""}${
                      loading ? " is-loading" : ""
                    }`}
                    onClick={() => handleRowClick(service)}
                  >
                    {multiSelect && (
                      <td className="col-check">
                        <div className="service-picker-checkbox">
                          {isSelected && <Check size={14} strokeWidth={3} />}
                        </div>
                      </td>
                    )}
                    <td className="col-id">
                      <div className="service-picker-id-cell">
                        <strong>#{service.id}</strong>
                        {enaexCode && <span className="service-picker-code">{enaexCode}</span>}
                      </div>
                    </td>
                    <td className="col-datetime">
                      <div className="service-picker-datetime">
                        <span>
                          <Calendar size={12} strokeWidth={2} aria-hidden />
                          {formatDate(service.service_date)}
                        </span>
                        <span>
                          <Clock size={12} strokeWidth={2} aria-hidden />
                          {formatTime(service.service_time)}
                        </span>
                      </div>
                    </td>
                    <td className="col-route">
                      <div className="service-picker-route">
                        <span className="service-picker-place" title={service.origin_name || undefined}>
                          <MapPin size={12} strokeWidth={2} aria-hidden />
                          {service.origin_name || "Sin origen"}
                        </span>
                        <span className="service-picker-arrow">→</span>
                        <span className="service-picker-place" title={service.destination_name || undefined}>
                          <MapPin size={12} strokeWidth={2} aria-hidden />
                          {service.destination_name || "Sin destino"}
                        </span>
                      </div>
                    </td>
                    <td className="col-passengers">
                      <div className="service-picker-passengers-cell">
                        <div className="service-picker-passengers-badge">
                          <Users size={12} strokeWidth={2} aria-hidden />
                          <span>{passengerInfo.count}</span>
                        </div>
                        <div 
                          className="service-picker-passengers-names" 
                          title={passengerInfo.names}
                        >
                          {passengerInfo.names}
                        </div>
                      </div>
                    </td>
                    <td className="col-driver">
                      <div className="service-picker-driver">
                        <UserRound size={12} strokeWidth={2} aria-hidden />
                        <span>{service.driver_name || "Sin asignar"}</span>
                      </div>
                    </td>
                    <td className="col-status">
                      <span className="service-picker-status">
                        {service.status || "—"}
                      </span>
                    </td>
                    <td className="col-actions">
                      {isSelected && onDeselect && (
                        <button
                          type="button"
                          className="service-picker-deselect-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (service.id) onDeselect(service.id);
                          }}
                          title="Quitar selección"
                        >
                          <X size={14} strokeWidth={2} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ServicePickerTable;
